
import { Card, CardHeader, CardTitle } from '@progress/kendo-react-layout';
import React, { useState } from 'react';
import LeasingEditor from './LeasingEditor/LeasingEditor';
import LeaseVehicleSaleEditor from './LeaseVehicleSaleEditor/LeaseVehicleSaleEditor';
import styles from './_lease-quote-editor.module.scss';
import LeaseDeferredDownEditor from "../LeaseDeferredDownEditor/LeaseDeferredDownEditor";
import LeaseLoanClassSelector from "../LeaseLoanClassSelector/LeaseLoanClassSelector";
import {useSelector} from "react-redux";
import {Typography} from '@mui/material';
import LeaseQuoterSolveButton from "../LeaseQuoterSolveButton/LeaseQuoterSolveButton";
import { Tooltip } from "@progress/kendo-react-tooltip";
import {IconButton} from "@mui/material";
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import classNames from "classnames";
import {ILeaseLoanClass} from "../../../../Interfaces/Common/ILeaseLoanClass";
import {IApplication} from "../../../../Interfaces/Application/IApplication";
import {IRuleViolation} from "../../../../Interfaces/Application/IApplicationQuote";
import {IConfiguration} from "../../../../Data/ConfigurationData";
import {ILookupItems} from "../../../../Interfaces/ILookupItems";
import {TabName} from "../../../Views/OrderEditor/OrderEditorTabs";
import {AppState} from "../../../../store/rootReducer";
import {getQuoteBackgroundColor} from "../../../../utils/quoteUtils";
import {authService} from "../../../../index";
import {httpPostAsync} from "../../../../utils/HttpClient";
import {saveFile} from "../../../../utils/utils";
import {VehicleButtonPanel, VehicleInfoComponent} from "../../../Views/OrderEditor/VehicleCard/VehicleInfoCard";
import {IQuotePayType} from "../../../../Interfaces/Common/IQuotePayType";
import {Button} from "@progress/kendo-react-buttons";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LeasePaymentScheduleDisplay from "../LeasePaymentScheduleDisplay/LeasePaymentScheduleDisplay";
import {getLeaseQuoteRefusal} from "../../../../utils/leaseQuoteSolveUtils";
import {useLeaseQuoteRefusal} from "../../../../hooks/UseLeaseQuoteRefusal";
import {useDialog} from "../../../../providers/DialogProvider";
import ErrorDialog from "../../../Dialogs/ErrorDialog";

export interface LeaseQuoteEditorProps {
  selectedLeaseLoanClass: ILeaseLoanClass | undefined;
  onChangeSingleField: (fieldName: string, value: number | Date | string | null | undefined) => void;
  currentApp: IApplication;
  applicationLeaseErrors: IRuleViolation[];
  configuration: IConfiguration;
  lookups: ILookupItems;
  leaseQuoteFieldIsDisabled: (fieldName: string) => boolean;
  navigate: (tabName: TabName) => void;
  // classes: Record<"titleText" | "noBorder", string>;
  selectedPaymentFrequency: IQuotePayType | undefined;
  handleChangeSelectedLeaseLoanClass: (newLoanClassId: string | undefined) => void;
}

export default function LeaseQuoteEditor(props: LeaseQuoteEditorProps) {
  const applicationLeaseQuoteIsDirty = useSelector((state: AppState) => state.applications.applicationLeaseQuoteIsDirty!);
  const applicationLeaseQuoteNeedsSolve = useSelector((state: AppState) => state.applications.applicationLeaseQuoteNeedsSolve!);
  const quoteBackgroundColor = getQuoteBackgroundColor(applicationLeaseQuoteIsDirty);
  const refuseLeaseQuote = useLeaseQuoteRefusal();
  const [openDialog, closeDialog] = useDialog();
  const scheduleNeedsSolve = !!getLeaseQuoteRefusal(props.currentApp, applicationLeaseQuoteIsDirty, applicationLeaseQuoteNeedsSolve,
    'printing the amortization schedule');

  const showScheduleError = (error: string) =>
    openDialog({ children: <ErrorDialog title="Amortization Schedule" errors={[error]} closeDialog={closeDialog} /> });

  // The server checks the quote again, builds a missing schedule and picks the report.
  const [isPrintingSchedule, setIsPrintingSchedule] = useState(false);
  const printAmortizationSchedule = async () => {
    if (refuseLeaseQuote('printing the amortization schedule')) {
      return;
    }
    setIsPrintingSchedule(true);
    try {
      const bearerToken = await authService.getToken();
      const report = await httpPostAsync(`api/ApplicationLeaseQuote/${props.currentApp.id}/AmortizationScheduleReport`, {}, bearerToken!);
      if (!report?.ok) {
        const message: string | undefined = await report?.json().then(body => body?.message).catch(() => undefined);
        showScheduleError(message ?? 'The amortization schedule could not be printed.');
        return;
      }
      await saveFile(report, 'LeaseAmortizationSchedule.pdf');
    } finally {
      setIsPrintingSchedule(false);
    }
  }

  const viewQuotePresentationReport = async () => {
    const bearerToken = await authService.getToken();

    const report = await httpPostAsync(`api/reporting/getReport`,
      {
        reportName: props.configuration.quotePresentationReport!,
        reportParameters: [{
          name: "AppNumber",
          value: props.currentApp.applicationNo,
        },
        ],
        reportFormat: "1"
      }, bearerToken!);

    saveFile(report!, 'report.pdf');
  }

  const saveApplicationRecapReport = async () => {
    const bearerToken = await authService.getToken();

    const recapReport = await httpPostAsync(`api/reporting/getReport`,
      {
        reportName: 'ApplicationRecap.trdp',
        reportParameters: [{
          name: "ApplicationNumber",
          value: props.currentApp.applicationNo,
        },
        ],
        reportFormat: "1"
      }, bearerToken!);

    // Sav
    saveFile(recapReport!, 'applicationRecap.pdf');
  }
  
  return (
    <div className={styles.leaseQuoteEditorContainer}>
      {/* Left Column */}
      <div className={styles.quoteEditorColumn}>
        <Card className={styles.quoteItemCard}>
          <CardHeader>
            <CardTitle>
              Loan Class
            </CardTitle>
          </CardHeader>
          <LeaseLoanClassSelector {...props} />
        </Card>
        <Card style={{ margin: '5px' }}>
          <CardHeader>
            <div className={styles.tileHeader}>
              <Typography>Vehicle</Typography>
              <div className={styles.buttonGroup}>
                <VehicleButtonPanel {...props} />
              </div>
            </div>
          </CardHeader>
          <VehicleInfoComponent {...props} hideCardHeader={true} />
        </Card>
      </div>
      
      {/* Center Column */}
      <div className={styles.quoteEditorColumn}>
        <Card className={classNames(styles.quoteItemCard, styles.financingHeader)}>
          <CardHeader>
            <div className={styles.tileHeader}>
              <Typography>Vehicle Sale</Typography>
            </div>
          </CardHeader>
          <LeaseVehicleSaleEditor {...props} />
        </Card>
          <LeaseDeferredDownEditor 
            {...props} 
            className={classNames(styles.quoteItemCard, styles.deferredDownHeader)}             
          />
      </div>
    
      {/* Right Column  */}
      <div className={styles.quoteEditorColumn}>
        <Card className={classNames(styles.quoteItemCard, styles.financingHeader)}>
          <CardHeader>
            <div className={styles.tileHeader}>
              <Typography>Leasing</Typography>
              <div className={styles.buttonGroup}>
                <LeaseQuoterSolveButton 
                  {...props} 
                  buttonName={"Quote / Solve"}
                />
                {props.configuration.applicationRecapReport &&
                  <Tooltip anchorElement="target" position="top" parentTitle={true}>
                    <IconButton
                      title={'Print Quote'}
                      style={{color: '#757575'}}
                      onClick={() => saveApplicationRecapReport()}
                    >
                      <LocalPrintshopIcon style={{fontSize: '30px'}} />
                    </IconButton>
                  </Tooltip>
                }
                {props.configuration.quotePresentationReport &&
                  <Tooltip anchorElement="target" position="top" parentTitle={true}>
                    <IconButton
                      title={'Print Quote'}
                      style={{color: '#757575'}}
                      onClick={() => viewQuotePresentationReport()}
                    >
                      <LocalPrintshopIcon style={{fontSize: '30px'}} />
                    </IconButton>
                  </Tooltip>
                }
                <Tooltip anchorElement="target" position="top" parentTitle={true}>
                  <Button
                    type="button"
                    fillMode="flat"
                    title={'Print Amortization Schedule'}
                    disabled={isPrintingSchedule}
                    onClick={() => printAmortizationSchedule()}
                  >
                    <CalendarMonthIcon style={{fontSize: '30px', color: '#757575'}} />
                  </Button>
                </Tooltip>
              </div>
            </div>
          </CardHeader>
          <LeasingEditor {...props}  />
        </Card>

      </div>

      <div className={styles.fullWidthSection}>
        <LeasePaymentScheduleDisplay
          quote={props.currentApp.applicationLeaseQuote}
          needsSolve={scheduleNeedsSolve}
          className={styles.scheduleCard}
        />
      </div>
      {/* <TileLayout
        columns={6}
        gap={{ rows: 10, columns: 10 }}
        rowHeight={165}
        style={{ width: '100%', backgroundColor: quoteBackgroundColor, minHeight: '1200px' }}
        items={tiles}
      /> */}
    </div>
  );
}

